/**
 * Drizl Brand Guidelines
 * 
 * Centralized brand colors, fonts, and design tokens
 * Source: /Users/lukasz/dev/LukaszKarpuk/drizl/src/brand
 */

export const colors = {
  // Primary Brand Colors
  primary: '#C8553D',      // Brand red (was #FF6B6B)
  secondary: '#F2853B',    // Brand orange (was #FF8E8E)
  tertiary: '#FFD5C2',     // Brand peach
  accent: '#558B8D',       // Brand green
  
  // Semantic Colors
  success: '#558B8D',      // Using brand green
  warning: '#F2853B',      // Using brand orange
  error: '#C8553D',        // Using brand red
  info: '#558B8D',         // Using brand green
  
  // Neutrals
  white: '#FFFFFF',
  black: '#000000',
  text: '#333333',
  textLight: '#666666',
  textMuted: '#999999',
  
  // Backgrounds
  background: '#FFFFFF',
  backgroundLight: '#F5F5F5',
  backgroundPeach: '#FFD5C2',
  
  // Borders & Dividers
  border: '#DDDDDD',
  borderLight: '#E0E0E0',
  
  // Interactive States
  hover: '#B04731',        // Darker primary for hover
  focus: '#558B8D',        // Green for focus states
  disabled: '#E5E7EB',
  
  // Status Colors (using brand palette)
  statusPending: '#F2853B',
  statusConfirmed: '#558B8D',
  statusPreparing: '#C8553D',
  statusDelivering: '#F2853B',
  statusDelivered: '#558B8D',
  statusCancelled: '#999999',
} as const;

export const fonts = {
  regular: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  heading: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  body: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
} as const;

export const fontSizes = {
  caption: '12px',      // 12px
  paragraph: '14px',    // 14px
  body: '16px',         // Default body text
  subheadline: '18px',  // 18px
  headline: '24px',     // 24px
  callout: '32px',      // 32px
  hero: '48px',         // Large hero text
} as const;

export const fontWeights = {
  regular: 400,         // Poppins Regular
  medium: 500,          // Poppins Medium
  semibold: 600,        // Poppins SemiBold
  bold: 700,            // Bold variant
  extrabold: 800,       // Poppins ExtraBold
} as const;

export const lineHeights = {
  caption: '18px',      // 18px
  paragraph: '21px',    // 21px
  body: '24px',         // Default
  subheadline: '27px',  // 27px
  headline: '35px',     // 35px
  callout: '48px',      // 48px
} as const;

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '40px',
  xxxl: '80px',
} as const;

export const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  round: '50%',
  pill: '9999px',
} as const;

export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 3px 6px 0 rgba(0, 0, 0, 0.1)',
  lg: '0 5px 15px 0 rgba(0, 0, 0, 0.1)',
  xl: '0 7px 25px 0 rgba(0, 0, 0, 0.15)',
  xxl: '0 12px 40px 0 rgba(0, 0, 0, 0.2)',
  none: 'none',
} as const;

export const breakpoints = {
  mobile: '320px',
  tablet: '768px',
  desktop: '1024px',
  wide: '1440px',
} as const;

export const transitions = {
  fast: '0.15s ease',
  normal: '0.3s ease',
  slow: '0.4s ease',
} as const;

// Theme object combining all design tokens
export const theme = {
  colors,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  spacing,
  borderRadius,
  shadows,
  breakpoints,
  transitions,
} as const;

// Type exports for TypeScript
export type Colors = typeof colors;
export type Theme = typeof theme;

export default theme;
